import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FollowUpCardController } from 'fragments/follow-up-card/interfaces';
import { CompleteFollowUpDto } from 'services/followUps/followUp.dto';

export const useFollowUpCardController = (
    dto: CompleteFollowUpDto,
    onFollowUpUpdate?: (dto: CompleteFollowUpDto) => void,
): FollowUpCardController => {
    /* State */
    const [followUp, setFollowUpState] = useState(dto);
    const [tagColor, setTagColor] = useState('');
    const [tagName, setTagName] = useState('');
    const [tagClassName, setTagClassName] = useState('');

    // Wraps the raw setState setter so every change made from inside the card (the payment fragment's
    // red/green icon and amount field, via FollowUpContext) also reaches the list that owns the row,
    // which is what lets the list re-sort the card into its pending/paid bucket without a refetch.
    const setFollowUp: Dispatch<SetStateAction<CompleteFollowUpDto>> = (update) => {
        setFollowUpState((prevState) => {
            const nextState =
                typeof update === 'function'
                    ? (update as (prev: CompleteFollowUpDto) => CompleteFollowUpDto)(prevState)
                    : update;
            onFollowUpUpdate?.(nextState);
            return nextState;
        });
    };

    /* Listeners */
    useEffect(() => {
        calculateTag();
    }, [followUp]);

    /* View Events */
    const onUpdate = (dto: CompleteFollowUpDto): void => {
        // This path only mirrors a new parent prop into the card's local state. Bubbling it back to
        // the parent creates a render loop: parent replaces dto -> this effect runs -> parent replaces
        // dto again. User actions still use setFollowUp above and continue to notify the list.
        setFollowUpState(dto);
    };

    /* Private Methods */
    const calculateTag = () => {
        if (followUp.followUpStatus === 'isPaid') {
            setTagName('Pagado');
            // Exact hex the design asked for — antd's 'green' preset doesn't match it precisely.
            setTagColor('#46CD32');
            setTagClassName('follow-up-status-tag');
        } else if (followUp.followUpStatus === 'expired') {
            setTagName('En mora');
            setTagColor('red');
            setTagClassName('follow-up-status-tag');
        } else {
            setTagName('Pendiente');
            setTagColor('default');
            setTagClassName('follow-up-status-tag');
        }
    };

    // Return state and events
    return {
        ownerName: `${followUp.ownerName}`,
        renterName: `${followUp.renterName}`,
        tagName,
        tagClassName,
        tagColor,
        title: followUp.alias ?? '-',
        followUp,
        setFollowUp,
        onUpdate,
    };
};
